/**
 * Vault context for global vault selection state
 */

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useVaults } from "../hooks/useVault";
import { VaultSummary } from "../api/vaults";

interface VaultContextType {
  currentVaultId: number | null;
  setCurrentVaultId: (id: number) => void;
  currentVault: VaultSummary | null;
  vaults: VaultSummary[];
  isLoading: boolean;
}

const VaultContext = createContext<VaultContextType | undefined>(undefined);

const VAULT_STORAGE_KEY = "cavefamily_selected_vault_id";

export function VaultProvider({ children }: { children: ReactNode }) {
  const { data: vaults = [], isLoading } = useVaults();
  const [currentVaultId, setCurrentVaultIdState] = useState<number | null>(() => {
    // Load from localStorage on mount
    const stored = localStorage.getItem(VAULT_STORAGE_KEY);
    return stored ? parseInt(stored, 10) : null;
  });

  // Auto-select first vault if none selected (only run on initial mount)
  useEffect(() => {
    if (!currentVaultId && vaults.length > 0) {
      const stored = localStorage.getItem(VAULT_STORAGE_KEY);
      if (stored) {
        const storedId = parseInt(stored, 10);
        // Verify the stored vault still exists
        if (vaults.some(v => v.id === storedId)) {
          setCurrentVaultIdState(storedId);
          return;
        }
      }
      // Only set to first vault if no stored value or stored vault doesn't exist
      setCurrentVaultIdState(vaults[0].id);
      localStorage.setItem(VAULT_STORAGE_KEY, vaults[0].id.toString());
    }
  }, [currentVaultId, vaults]);

  const setCurrentVaultId = (id: number) => {
    setCurrentVaultIdState(id);
    localStorage.setItem(VAULT_STORAGE_KEY, id.toString());
  };

  const currentVault = vaults.find((v) => v.id === currentVaultId) || null;

  return (
    <VaultContext.Provider
      value={{
        currentVaultId,
        setCurrentVaultId,
        currentVault,
        vaults,
        isLoading,
      }}
    >
      {children}
    </VaultContext.Provider>
  );
}

// Hook is exported from same file as provider for ergonomics; fast-refresh warning accepted
// eslint-disable-next-line react-refresh/only-export-components
export function useVaultContext() {
  const context = useContext(VaultContext);
  if (context === undefined) {
    throw new Error("useVaultContext must be used within a VaultProvider");
  }
  return context;
}
